import { FC, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  assignOrder,
  exportOrder,
  getOrderArticlesStock,
  getOrderById,
  getOrderEmailHistory,
  getOrderHistory,
  getPdfVerification,
} from "../../app/services/orderService";
import { checkStockStatus } from "../../app/services/articleService";
import { useAppDispatch } from "../../app/hooks";
import { useAccount, useMsal } from "@azure/msal-react";
import { useSelector } from "react-redux";
import { RootState } from "../../app/store";
import Form from "react-bootstrap/Form";
import {
  Accordion,
  Alert,
  Badge,
  Breadcrumb,
  Button,
  Col,
  ListGroup,
  Modal,
  Row,
  Spinner,
  Table,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import moment from "moment";
import "moment/locale/sv";
import "./view.scss";
import {
  ChatText,
  Check,
  CheckAll,
  ExclamationTriangle,
  Eye,
  FileEarmarkArrowDown,
  FilePdf,
  PersonFillLock,
  X,
} from "react-bootstrap-icons";
import { Article, defaultArticle } from "../../models/responses/article";
import { defaultOrder, Order } from "../../models/responses/order";
import { getOrderStatusById } from "../../app/services/orderStatusService";
import {
  setClientToggleLocked,
  setClientType,
} from "../../app/slices/clientTypeSlice";
import { ClientType } from "../../models/enums/clientTypeEnum";
import { Supplier } from "../../models/responses/supplier";
import { getSuppliers } from "../../app/services/supplierService";
import { ArticleType } from "../../models/enums/articleTypeEnum";
import StandardPriceProposal from "../../components/standardPriceProposal.tsx/standardPriceProposal";
import FleetPriceProposal from "../../components/fleetPriceProposal/fleetPriceProposal";

interface Props {}

const OrderView: FC<Props> = () => {
  const [showStockInfo, setShowStockInfo] = useState(false);
  const [articleStockStatus, setArticleStockStatus] = useState<
    Record<string, any>
  >({});
  const [showStockErrorModal, setShowStockErrorModal] = useState(false);
  moment.locale("sv");
  moment.updateLocale(moment.locale(), { invalidDate: "-" });
  const { orderId } = useParams<{ orderId: string }>();
  const { orderStatusId } = useParams<{ orderStatusId: string }>();
  const dispatch = useAppDispatch();
  const { instance, accounts } = useMsal();
  const account = useAccount(accounts[0] || {});
  const order = useSelector((state: RootState) => state.order.order);
  const pdfVerification = useSelector(
    (state: RootState) => state.order.pdfVerification,
  );
  const orderStatus = useSelector(
    (state: RootState) => state.orderStatus.orderStatus,
  );
  const loadingOrders: boolean = useSelector(
    (state: RootState) => state.order.isWaitingOrderGetResponse,
  );
  const [orderData, setOrderData] = useState<Order>({ ...defaultOrder });
  // const [pdfVerification, setPdfVerification] = useState<Blob | undefined>(undefined);
  const [showArticleModal, setShowArticleModal] = useState(false);
  const [modalArticle, setModalArticle] = useState<Article>({
    ...defaultArticle,
  });
  const [allProductVerified, setAllProductVerified] = useState(false);
  const [allTimeVerified, setAllTimeVerified] = useState(false);
  const [showExportModal, setShowExportModel] = useState(false);
  const [allowedToExport, setAllowedToExport] = useState(false);
  const [supplierExists, setSupplierExists] = useState(false);
  const [displayError, setDisplayError] = useState(false);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const clientType = useSelector(
    (state: RootState) => state.clientType.clientType,
  );
  const handleCloseCustomerModal = () => setShowCustomerModal(false);
  const handleShowCustomerModal = () => setShowCustomerModal(true);
  const suppliers: Supplier[] = useSelector(
    (state: RootState) => state.supplier.supplierList,
  );
  const emailHistory = useSelector(
    (state: RootState) => state.order.emailHistory,
  );

  useEffect(() => {
    dispatch(setClientToggleLocked(true));
    dispatch(getOrderStatusById(instance, account, orderStatusId));
  }, [orderStatusId, account, accounts, dispatch, instance]);

  useEffect(() => {}, [orderStatus]);

  useEffect(() => {
    dispatch(getOrderById(instance, account, orderId));
  }, [account, accounts, dispatch, instance, orderId]);

  useEffect(() => {
    if (order && order.id) {
      setOrderData({ ...order });
      if (clientType !== order.clientType) {
        dispatch(setClientType(order.clientType));
      }
    }
  }, [order, clientType, dispatch]);

  useEffect(() => {
    checkVerified();
  }, [order, orderData.articles]);

  useEffect(() => {
    if (!order) return;
    checkIfAllowedToExport();
  }, [
    order,
    orderData,
    orderData.articles,
    allProductVerified,
    allTimeVerified,
    supplierExists,
  ]);

  useEffect(() => {
    if (!order) return;
    checkIfSupplierExists();
  }, [order, orderData, orderData.articles]);

  useEffect(() => {
    setModalArticle((prev) => ({
      ...prev,
      totalExVat: parseFloat(
        (parseFloat(prev.salesPrice) * prev.quantity).toFixed(2),
      ),
    }));
  }, [modalArticle.quantity, modalArticle.salesPrice, modalArticle.vat]);

  useEffect(() => {
    dispatch(getSuppliers(instance, account, clientType));
  }, [account, accounts, dispatch, instance, clientType]);

  useEffect(() => {
    dispatch(getOrderEmailHistory(instance, account, orderId));
  }, [account, accounts, dispatch, instance, orderId]);

  useEffect(() => {
    if (pdfVerification) {
      const blob = new Blob([pdfVerification], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "test_file.pdf");
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
    }
  }, [pdfVerification]);

  const isWaitingOrderExportResponse = useSelector(
    (state: RootState) => state.order.isWaitingOrderExportResponse,
  );

  const isWaitingSendOrderEmailResponse = useSelector(
    (state: RootState) => state.order.isWaitingSendOrderEmailResponse,
  );

  const isWaitingPriceProposalPreviewResponse = useSelector(
    (state: RootState) => state.order.isWaitingPriceProposalPreviewResponse,
  );

  const isWaitingStockResponse = useSelector(
    (state: RootState) => state.order.isWaitingStockResponse,
  );

  const checkVerified = () => {
    const productArticles =
      orderData.articles?.filter(
        (art) => art.articleType === ArticleType.Product,
      ) ?? [];
    const timeArticles =
      orderData.articles?.filter(
        (art) => art.articleType === ArticleType.Time,
      ) ?? [];
    setAllProductVerified(
      productArticles.length === 0 ||
        productArticles.every((art) => art.verified),
    );
    setAllTimeVerified(
      timeArticles.length === 0 || timeArticles.every((art) => art.verified),
    );
  };

  const openArticleModalHandler = (article: Article) => {
    setModalArticle({ ...modalArticle, ...article });
    setShowArticleModal(true);
  };

  const handleExportOrder = async () => {
    setDisplayError(false);
    setShowExportModel(false);
    if (clientType === ClientType.Fleet) {
      await dispatch(getOrderArticlesStock(instance, account, orderId));
    }
    await dispatch(
      exportOrder(instance, account, orderId, handleExportOrderSuccess),
    );
  };

  const handleExportOrderSuccess = async (
    orderId: string | null,
    isUnprocessableEntity?: boolean,
  ) => {
    if (isUnprocessableEntity && orderData.articles) {
      setShowStockErrorModal(true);
    } else {
      setShowStockInfo(false);
      setArticleStockStatus({});
      if (orderId) {
        setOrderData((prev) => ({ ...prev, exported: true }));
      }
    }
  };

  const getStockStatusMessage = (status: any): string => {
    if (!status) return "";
    if (typeof status === "object") {
      if (status.error) return "Fel vid hämtning";
      if (status.message) return status.message;
      return JSON.stringify(status);
    }
    if (typeof status === "string") return status;
    return "";
  };

  const handleConfirmStockError = async () => {
    setShowStockErrorModal(false);
    setShowStockInfo(true);
    if (orderData.articles) {
      const articlesWithId = orderData.articles.filter(
        (art) => typeof art.id === "string" && art.id,
      );
      const results = await Promise.all(
        articlesWithId.map(async (art) => {
          try {
            const result: any = await dispatch(
              checkStockStatus(instance, account, art.id as string),
            );
            const payload =
              result && typeof result === "object" && "payload" in result
                ? result.payload
                : result;
            return [art.id, payload];
          } catch (e) {
            return [art.id, { error: true }];
          }
        }),
      );
      setArticleStockStatus(Object.fromEntries(results));
    }
  };

  const closeArticleModalHandler = () => {
    setShowArticleModal(false);
  };

  const handleAssignOrder = () => {
    if (account) {
      dispatch(assignOrder(instance, account, orderId));
      setOrderData({
        ...orderData,
        userId: account.localAccountId || "",
        userName: account.name || "",
      });
    }
  };

  const handleCheckStock = () => {
    dispatch(getOrderArticlesStock(instance, account, orderId));
    // setHasUpdatedStock(true);
  };

  const checkIfAllowedToExport = () => {
    let allowed = true;
    if (!orderData) return;

    if (!orderData.articles || orderData.articles.length <= 0) allowed = false;

    if (!allProductVerified || !allTimeVerified) allowed = false;

    if (!orderData.registrationNumber || orderData.registrationNumber === "")
      allowed = false;

    if (orderData.clientType === ClientType.Standard) {
      if (!orderData.aoNumber || orderData.aoNumber === "") {
        allowed = false;
      }
    } else if (orderData.clientType === ClientType.Catalog) {
      if (!orderData.externalOrderId) allowed = false;
    }

    if (!supplierExists) allowed = false;

    setAllowedToExport(allowed);
  };

  const checkIfSupplierExists = () => {
    let exists = true;

    if (order.clientType !== ClientType.Fleet && !orderData.supplierId) {
      if (!orderData.articles) exists = false;
      else {
        const productArticles = orderData.articles.filter(
          (art) => art.articleType === ArticleType.Product,
        );
        let allHaveSupplier = productArticles.every((art) => !!art.supplierId);
        if (!allHaveSupplier) exists = false;
      }
    }
    setSupplierExists(exists);
  };

  const [historyLoaded, setHistoryLoaded] = useState(false);
  const handleHistoryAccordion = async () => {
    if (!historyLoaded && orderId) {
      dispatch(getOrderHistory(instance, account, orderId));
      setHistoryLoaded(true);
    }
  };

  const handleCloseExportModal = () => setShowExportModel(false);
  const handleShowExportModal = () => setShowExportModel(true);

  const handlePdfVerification = () => {
    dispatch(getPdfVerification(instance, account, orderId));
  };

  return (
    <div>
      <Modal
        show={isWaitingOrderExportResponse}
        centered
        backdrop="static"
        keyboard={false}
      >
        <Modal.Body className="text-center">
          <Spinner animation="border" role="status" className="mb-2" />
          <div>Vänligen vänta, exporterar order...</div>
        </Modal.Body>
      </Modal>

      {loadingOrders ? (
        <>
          <Row>
            <Col className="text-middle">
              <Spinner variant="primary" />
            </Col>
          </Row>
        </>
      ) : (
        <>
          <Breadcrumb>
            <Breadcrumb.Item href={`/orderStatus/${orderData.orderStatus?.id}`}>
              {orderData.orderStatus?.name}
            </Breadcrumb.Item>
            <Breadcrumb.Item active>{orderData.customer?.name}</Breadcrumb.Item>
          </Breadcrumb>
          <hr />
          <Form>
            <Row>
              <Col xs={12} md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Status</Form.Label>
                  <Form.Control
                    type="text"
                    value={orderData.orderStatus?.name || ""}
                    readOnly
                    disabled
                  />
                </Form.Group>
              </Col>
              {orderData.approved &&
                orderData.clientType === ClientType.Catalog && (
                  <Col xs={12} md={3}>
                    <div className="export-badge">
                      <Badge bg="success"> Ordern är godkänd i katalogen</Badge>
                    </div>
                  </Col>
                )}
              <Col xs={12} md={2}>
                {orderData.exported && (
                  <div className="export-badge">
                    <Badge bg="success"> Ordern är exporterad till AX</Badge>
                  </div>
                )}
              </Col>
              <Col xs={12} md={5}>
                {orderStatus.orderPageType &&
                  orderStatus.orderPageType.fileName && (
                    <>
                      {orderData.filename && (
                        <>
                          <br />
                          <Button
                            variant="outline-primary"
                            className="mt-2 ms-2 float-end"
                            target="_blank"
                            href={`${orderData.filepath}`}
                          >
                            Hämta orderfil <FileEarmarkArrowDown />
                          </Button>
                        </>
                      )}
                    </>
                  )}
              </Col>
            </Row>
            <Row>
              {orderStatus.orderPageType &&
                orderStatus.orderPageType.customer && (
                  <Col xs={12} md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        Kund{" "}
                        <Badge
                          bg="primary"
                          onClick={handleShowCustomerModal}
                          role="button"
                        >
                          Visa kundinfo
                        </Badge>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        value={orderData.customer?.name || ""}
                        readOnly
                        disabled
                      />
                    </Form.Group>
                  </Col>
                )}
              {orderStatus.orderPageType &&
                orderStatus.orderPageType.assignedTo && (
                  <Col xs={12} md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Ansvarig</Form.Label>
                      <Form.Control
                        type="text"
                        value={orderData.assignedTo}
                        readOnly
                        disabled
                      />
                    </Form.Group>
                  </Col>
                )}
              {orderStatus.orderPageType &&
                orderStatus.orderPageType.registrationNumber && (
                  <Col xs={12} md={2}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        Registreringsnummer{" "}
                        {(!orderData.registrationNumber ||
                          orderData.registrationNumber === "") && (
                          <Badge bg="danger">Krävs för export</Badge>
                        )}
                      </Form.Label>
                      <Form.Control
                        type="text"
                        value={orderData.registrationNumber}
                        readOnly
                        disabled
                      />
                    </Form.Group>
                  </Col>
                )}
              {clientType === ClientType.Catalog && (
                <Col xs={12} md={2}>
                  <Form.Group className="mb-3">
                    <Form.Label>Bilmärke </Form.Label>
                    <Form.Control
                      type="text"
                      value={orderData.selectedCarBrand?.name || ""}
                      readOnly
                      disabled
                    />
                  </Form.Group>
                </Col>
              )}
            </Row>
            <Row>
              {orderStatus.orderPageType && orderStatus.orderPageType.vin && (
                <Col xs={12} md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      VIN
                      {orderData.vinVerified ? (
                        <>
                          {" "}
                          <Badge bg="success">Kontrollerad</Badge>
                        </>
                      ) : (
                        <>
                          {" "}
                          <Badge bg="warning">Inte kontrollerad</Badge>
                        </>
                      )}
                      {orderData.correctedVin && (
                        <>
                          {" "}
                          <Badge bg="success">VIN har korrigerats</Badge>
                        </>
                      )}
                    </Form.Label>
                    <Form.Control
                      type="text"
                      value={orderData.vin}
                      readOnly
                      disabled
                    />
                  </Form.Group>
                </Col>
              )}
              {orderStatus.orderPageType &&
                orderStatus.orderPageType.carInfo && (
                  <Col xs={12} md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Bilmärke</Form.Label>
                      <Form.Control
                        type="text"
                        value={orderData.carInfo}
                        readOnly
                        disabled
                      />
                    </Form.Group>
                  </Col>
                )}
              {order.clientType === ClientType.Standard && (
                <>
                  {orderStatus.orderPageType &&
                    orderStatus.orderPageType.aoNumber && (
                      <Col xs={12} md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>
                            AO-nummer{" "}
                            {(!orderData.aoNumber ||
                              orderData.aoNumber === "") && (
                              <Badge bg="danger">Krävs för export</Badge>
                            )}
                          </Form.Label>
                          <Form.Control
                            type="text"
                            value={orderData.aoNumber}
                            readOnly
                            disabled
                          />
                        </Form.Group>
                      </Col>
                    )}
                </>
              )}
              {order.clientType === ClientType.Catalog && (
                <>
                  {orderStatus.orderPageType &&
                    orderStatus.orderPageType.externalOrderId && (
                      <Col xs={12} md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>
                            Order.nr{" "}
                            {(!orderData.externalOrderId ||
                              orderData.externalOrderId === 0) && (
                              <Badge bg="danger">Krävs för export</Badge>
                            )}
                          </Form.Label>
                          <Form.Control
                            type="text"
                            value={orderData.externalOrderId || ""}
                            readOnly
                            disabled
                          />
                        </Form.Group>
                      </Col>
                    )}
                </>
              )}
            </Row>
            <Row>
              {orderStatus.orderPageType &&
                orderStatus.orderPageType.foNumber && (
                  <Col xs={12} md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>FO-nummer</Form.Label>
                      <Form.Control
                        type="text"
                        value={orderData.salesOrderNumber}
                        readOnly
                        disabled
                      />
                    </Form.Group>
                  </Col>
                )}
              {orderStatus.orderPageType &&
                orderStatus.orderPageType.ioNumber && (
                  <Col xs={12} md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>IO-nummer</Form.Label>
                      <Form.Control
                        type="text"
                        value={orderData.purchaseOrderNumber}
                        readOnly
                        disabled
                      />
                    </Form.Group>
                  </Col>
                )}
              {orderStatus.orderPageType &&
                orderStatus.orderPageType.orderType && (
                  <>
                    <Col xs={12} md={2}>
                      <Form.Group className="mb-3">
                        <Form.Label>Ordertyp</Form.Label>
                        <Form.Control
                          type="text"
                          value={orderData.orderType?.name || ""}
                          readOnly
                          disabled
                        />
                      </Form.Group>
                    </Col>
                    {order.clientType === ClientType.Standard && (
                      <Col xs={12} md={2}>
                        <Form.Group className="mb-3">
                          <Form.Label>Rabatt (%)</Form.Label>
                          <Form.Control
                            type="number"
                            value={orderData.discount ?? ""}
                            readOnly
                            disabled
                          />
                        </Form.Group>
                      </Col>
                    )}
                  </>
                )}

              {orderStatus.orderPageType &&
                orderStatus.orderPageType.orderedBy && (
                  <Col xs={12} md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Beställd av</Form.Label>
                      <Form.Control
                        type="text"
                        value={orderData.orderedBy}
                        readOnly
                        disabled
                      />
                    </Form.Group>
                  </Col>
                )}
              {orderStatus.orderPageType &&
                orderStatus.orderPageType.followUp && (
                  <Col xs={12} md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Uppföljning</Form.Label>
                      <Form.Control
                        type="text"
                        value={orderData.followUp}
                        readOnly
                        disabled
                      />
                    </Form.Group>
                  </Col>
                )}
              {orderStatus.orderPageType &&
                orderStatus.orderPageType.emailBody &&
                clientType === ClientType.Standard && (
                  <Col xs={12} md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>E-post innehåll</Form.Label>
                      <Form.Control
                        type="text"
                        value={orderData.emailBody}
                        readOnly
                        disabled
                      />
                    </Form.Group>
                  </Col>
                )}

              {orderStatus.orderPageType &&
                orderStatus.orderPageType.warehouseLocation && (
                  <Col xs={12} md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Lagerplats</Form.Label>
                      <Form.Control
                        type="text"
                        value={orderData.warehouseLocation}
                        readOnly
                        disabled
                      />
                    </Form.Group>
                  </Col>
                )}
              {(clientType === ClientType.Catalog ||
                (orderStatus.orderPageType &&
                  orderStatus.orderPageType.supplier)) && (
                <Col xs={12} md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Leverantör</Form.Label>
                    <Form.Control
                      type="text"
                      value={orderData.supplier?.name || ""}
                      readOnly
                      disabled
                    />
                  </Form.Group>
                </Col>
              )}
              {orderStatus.orderPageType &&
                orderStatus.orderPageType.arriveDate && (
                  <Col xs={12} md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Inkommen</Form.Label>
                      <Form.Control
                        type="text"
                        value={moment(orderData.arriveDate).format(
                          "yyyy-MM-DD",
                        )}
                        readOnly
                        disabled
                      />
                    </Form.Group>
                  </Col>
                )}

              {orderStatus.orderPageType &&
                orderStatus.orderPageType.bookingDate && (
                  <Col xs={12} md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Bokad (prod.dag)</Form.Label>
                      <Form.Control
                        type="text"
                        value={moment(orderData.bookingDate).format(
                          "yyyy-MM-DD",
                        )}
                        readOnly
                        disabled
                      />
                    </Form.Group>
                  </Col>
                )}
              {orderStatus.orderPageType &&
                orderStatus.orderPageType.deliveryDate && (
                  <Col xs={12} md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Leveransdatum</Form.Label>
                      <Form.Control
                        type="text"
                        value={moment(orderData.deliveryDate).format(
                          "yyyy-MM-DD",
                        )}
                        readOnly
                        disabled
                      />
                    </Form.Group>
                  </Col>
                )}
              {orderStatus.orderPageType &&
                orderStatus.orderPageType.pickupTime && (
                  <Col xs={12} md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Upphämtningsdatum</Form.Label>
                      <Form.Control
                        type="text"
                        value={moment(orderData.pickupDate).format(
                          "yyyy-MM-DD",
                        )}
                        readOnly
                        disabled
                      />
                    </Form.Group>
                  </Col>
                )}

              {orderStatus.orderPageType &&
                orderStatus.orderPageType.orderDate && (
                  <Col xs={12} md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Beställt</Form.Label>
                      <Form.Control
                        type="text"
                        value={moment(orderData.orderDate).format("yyyy-MM-DD")}
                        readOnly
                        disabled
                      />
                    </Form.Group>
                  </Col>
                )}
              {clientType === ClientType.Standard && (
                <>
                  {orderStatus.orderPageType &&
                    orderStatus.orderPageType.fileName && (
                      <Col xs={12} md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>Filnamn</Form.Label>
                          <Form.Control
                            type="text"
                            value={orderData.filename}
                            readOnly
                            disabled
                          />
                        </Form.Group>
                      </Col>
                    )}
                  {orderStatus.orderPageType &&
                    orderStatus.orderPageType.carUrl && (
                      <Col xs={12} md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>PL URL</Form.Label>
                          <div className="mt-2">
                            {orderData.carUrl &&
                            orderData.carUrl.trim() !== "" ? (
                              <Link
                                target="_blank"
                                className="mt-1"
                                to={orderData.carUrl}
                              >
                                Länk
                              </Link>
                            ) : (
                              <span>-</span>
                            )}
                          </div>
                        </Form.Group>
                      </Col>
                    )}
                </>
              )}
            </Row>
            <Row>
              {orderStatus.orderPageType &&
                orderStatus.orderPageType.comment && (
                  <Col xs={12} md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Kommentar</Form.Label>
                      <Form.Control
                        as="textarea"
                        value={orderData.comment}
                        readOnly
                        disabled
                      />
                    </Form.Group>
                  </Col>
                )}
              {orderStatus.orderPageType &&
                orderStatus.orderPageType.internalComment && (
                  <Col xs={12} md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Intern kommentar</Form.Label>
                      <Form.Control
                        as="textarea"
                        value={orderData.internalComment}
                        readOnly
                        disabled
                      />
                    </Form.Group>
                  </Col>
                )}
            </Row>
            {clientType === ClientType.Fleet && (
              <Row>
                <Col xs={12} md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Meddelande på prisförslag</Form.Label>
                    <Form.Control
                      as="textarea"
                      value={orderData.priceProposalMessage ?? ""}
                      readOnly
                      disabled
                    />
                  </Form.Group>
                </Col>
              </Row>
            )}
            {(clientType === ClientType.Catalog ||
              clientType === ClientType.Fleet) && (
              <>
                <Row>
                  <Col xs={12} md={6}>
                    <Form.Group className="mb-3">
                      <Form.Check
                        type="switch"
                        label="Godkänd av kund"
                        className="mt-2"
                        checked={orderData.approved || false}
                        readOnly
                        disabled
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </>
            )}
            {clientType === ClientType.Fleet && (
              <>
                <Row>
                  <Col xs={12} md={6}>
                    <Form.Group className="mb-3">
                      <Form.Check
                        type="switch"
                        label="Nekad av kund"
                        className="mt-2"
                        checked={orderData.denied || false}
                        readOnly
                        disabled
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </>
            )}
            {clientType === ClientType.Fleet && (
              <>
                <Row>
                  <Col xs={12} md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Ordernummer hos Postnord</Form.Label>
                      <Form.Control
                        type="text"
                        value={orderData.clientExternalOrderId ?? ""}
                        readOnly
                        disabled
                      />
                    </Form.Group>
                  </Col>
                  <Col xs={12} md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Miltal</Form.Label>
                      <Form.Control
                        type="text"
                        value={orderData.odometer ?? ""}
                        readOnly
                        disabled
                      />
                    </Form.Group>
                  </Col>
                  <Col xs={12} md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Hämtad av</Form.Label>
                      <Form.Control
                        type="text"
                        value={orderData.clientVehicleRetriever ?? ""}
                        readOnly
                        disabled
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col xs={12} md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Verkstad</Form.Label>
                      <Form.Control
                        type="text"
                        value={`${orderData.workshopName ?? ""} - ${orderData.workshopId ?? ""}`}
                        readOnly
                        disabled
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </>
            )}
            {orderData.articles && orderData.articles.length > 0 && (
              <>
                <h3>Artiklar</h3>
                <Table responsive striped bordered hover>
                  <thead>
                    <tr key="-1">
                      <th>
                        {allProductVerified ? (
                          <CheckAll className="text-success" />
                        ) : (
                          <ExclamationTriangle className="text-danger" />
                        )}
                      </th>
                      {showStockInfo &&
                        orderData.articles &&
                        orderData.articles.some(
                          (article) =>
                            article.id &&
                            getStockStatusMessage(
                              articleStockStatus[article.id],
                            ),
                        ) && <th>Info</th>}
                      {orderStatus.orderPageType &&
                        orderStatus.orderPageType.articleTableArticleNumber && (
                          <th>Art.nr</th>
                        )}
                      {orderStatus.orderPageType &&
                        orderStatus.orderPageType.articleTableDescription && (
                          <th>Beskrivning</th>
                        )}
                      {orderStatus.orderPageType &&
                        orderStatus.orderPageType.articleTableQuantity && (
                          <th>Antal</th>
                        )}
                      {clientType === ClientType.Fleet && <th>Saldo</th>}
                      {orderStatus.orderPageType &&
                        orderStatus.orderPageType.articleTableSalesPrice && (
                          <th>Försäljningspris</th>
                        )}
                      {orderStatus.orderPageType &&
                        orderStatus.orderPageType.articleTableDiscount &&
                        (clientType === ClientType.Standard ||
                          clientType === ClientType.Fleet) && (
                          <th>Rabatt (%)</th>
                        )}
                      {orderStatus.orderPageType &&
                        orderStatus.orderPageType.articleTablePurchasePrice && (
                          <th>Inköpspris</th>
                        )}
                      {(clientType === ClientType.Catalog ||
                        (orderStatus.orderPageType &&
                          orderStatus.orderPageType
                            .articleTableSupplierName)) && <th>Leverantör</th>}
                      {orderStatus.orderPageType &&
                        orderStatus.orderPageType.articleTableIONumber && (
                          <th>IO-nummer</th>
                        )}
                      {orderStatus.orderPageType &&
                        orderStatus.orderPageType.articleTableComment && (
                          <th>Extern kommentar</th>
                        )}
                      {clientType === ClientType.Fleet && (
                        <>
                          <th>Exkluderad från order</th>
                        </>
                      )}
                      {orderStatus.orderPageType &&
                        orderStatus.orderPageType.articleTablePartUrl &&
                        clientType === ClientType.Standard && <th>PL</th>}
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {orderData?.articles
                      ?.filter(
                        (article) =>
                          article.articleType === ArticleType.Product,
                      )
                      .map((article) => (
                        <tr
                          role="button"
                          key={article.rowNumber}
                          onClick={() => openArticleModalHandler(article)}
                        >
                          <td>
                            {!article.verified ? (
                              <X className="text-danger" />
                            ) : (
                              <Check className="text-success" />
                            )}
                          </td>
                          {showStockInfo &&
                            article.id &&
                            getStockStatusMessage(
                              articleStockStatus[article.id],
                            ) && (
                              <td>
                                <OverlayTrigger
                                  placement="top"
                                  overlay={
                                    <Tooltip id={`tooltip-stock-${article.id}`}>
                                      {article.id != null
                                        ? getStockStatusMessage(
                                            articleStockStatus[article.id],
                                          )
                                        : ""}
                                    </Tooltip>
                                  }
                                >
                                  <span>
                                    <ChatText style={{ cursor: "pointer" }} />
                                  </span>
                                </OverlayTrigger>
                              </td>
                            )}
                          {orderStatus.orderPageType &&
                            orderStatus.orderPageType
                              .articleTableArticleNumber && (
                              <td>{article.articleNumber}</td>
                            )}
                          {orderStatus.orderPageType &&
                            orderStatus.orderPageType
                              .articleTableDescription && (
                              <td>{article.description}</td>
                            )}
                          {orderStatus.orderPageType &&
                            orderStatus.orderPageType.articleTableQuantity && (
                              <td>{article.quantity}</td>
                            )}
                          {clientType === ClientType.Fleet && (
                            <>
                              <td align="left">{article.stock ?? "-"}</td>
                            </>
                          )}
                          {orderStatus.orderPageType &&
                            orderStatus.orderPageType
                              .articleTableSalesPrice && (
                              <td>{article.salesPrice}</td>
                            )}
                          {orderStatus.orderPageType &&
                            orderStatus.orderPageType.articleTableDiscount &&
                            (clientType === ClientType.Standard ||
                              clientType === ClientType.Fleet) && (
                              <td>{article.discount}</td>
                            )}
                          {orderStatus.orderPageType &&
                            orderStatus.orderPageType
                              .articleTablePurchasePrice && (
                              <td>
                                {article.purchasePrice}{" "}
                                {article.supplierId
                                  ? suppliers.find(
                                      (s) => s.id === article.supplierId,
                                    )?.currency
                                  : suppliers.find(
                                      (s) => s.id === orderData.supplierId,
                                    )?.currency}
                              </td>
                            )}
                          {(clientType === ClientType.Catalog ||
                            (orderStatus.orderPageType &&
                              orderStatus.orderPageType
                                .articleTableSupplierName)) && (
                            <td>{article.supplier?.name}</td>
                          )}
                          {orderStatus.orderPageType &&
                            orderStatus.orderPageType.articleTableIONumber && (
                              <td>{article.purchaseOrderNumber}</td>
                            )}
                          {orderStatus.orderPageType &&
                            orderStatus.orderPageType.articleTableComment && (
                              <td>{article.comment}</td>
                            )}
                          {clientType === ClientType.Fleet && (
                            <>
                              <td align="center">
                                {article.excludeFromWarehousePicking ? (
                                  <span className="text-danger">
                                    Beställ ej
                                  </span>
                                ) : (
                                  <span>-</span>
                                )}
                              </td>
                            </>
                          )}
                          {orderStatus.orderPageType &&
                            orderStatus.orderPageType.articleTablePartUrl &&
                            clientType === ClientType.Standard && (
                              <td>
                                {!article.partUrl ? (
                                  <span>-</span>
                                ) : (
                                  <Link
                                    onClick={(e) => e.stopPropagation()}
                                    target="_blank"
                                    to={article.partUrl ?? ""}
                                  >
                                    <Check className="text-success" />
                                  </Link>
                                )}
                              </td>
                            )}

                          <td align="center">
                            <Eye />
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </Table>

                {orderData.articles.some(
                  (article) => article.articleType === ArticleType.Time,
                ) && (
                  <>
                    <h3>Reparationstider</h3>
                    <Table responsive striped bordered hover>
                      <thead>
                        <tr>
                          <th>
                            {allTimeVerified ? (
                              <CheckAll className="text-success" />
                            ) : (
                              <ExclamationTriangle className="text-danger" />
                            )}
                          </th>
                          {showStockInfo &&
                            orderData.articles &&
                            orderData.articles.some(
                              (article) =>
                                article.id &&
                                getStockStatusMessage(
                                  articleStockStatus[article.id],
                                ),
                            ) && <th>Info</th>}
                          <th>Art.nr</th>
                          <th>Beskrivning</th>
                          <th>Antal</th>
                          <th>Försäljningspris</th>
                          <th>Tid</th>
                        </tr>
                      </thead>
                      <tbody>
                        {orderData.articles
                          .filter(
                            (article) =>
                              article.articleType === ArticleType.Time,
                          )
                          .map((article) => (
                            <tr key={article.rowNumber}>
                              <td>
                                {!article.verified ? (
                                  <X className="text-danger" />
                                ) : (
                                  <Check className="text-success" />
                                )}
                              </td>
                              {showStockInfo &&
                                article.id &&
                                getStockStatusMessage(
                                  articleStockStatus[article.id],
                                ) && (
                                  <td>
                                    <OverlayTrigger
                                      placement="top"
                                      overlay={
                                        <Tooltip
                                          id={`tooltip-stock-${article.id}`}
                                        >
                                          {article.id &&
                                          articleStockStatus[article.id] &&
                                          typeof articleStockStatus[
                                            article.id
                                          ] === "object" &&
                                          articleStockStatus[article.id].message
                                            ? articleStockStatus[article.id]
                                                .message
                                            : article.id &&
                                                articleStockStatus[
                                                  article.id
                                                ] &&
                                                typeof articleStockStatus[
                                                  article.id
                                                ] === "string"
                                              ? articleStockStatus[article.id]
                                              : ""}
                                        </Tooltip>
                                      }
                                    >
                                      <span>
                                        <ChatText
                                          style={{ cursor: "pointer" }}
                                        />
                                      </span>
                                    </OverlayTrigger>
                                  </td>
                                )}
                              <td>{article.articleNumber}</td>
                              <td>{article.description}</td>
                              <td>{article.quantity}</td>
                              <td>{article.salesPrice}</td>
                              <td>{article.time}</td>
                            </tr>
                          ))}
                      </tbody>
                    </Table>
                  </>
                )}
              </>
            )}
          </Form>
          <Modal
            size="xl"
            show={showArticleModal}
            onHide={() => closeArticleModalHandler()}
          >
            <Modal.Header closeButton>
              <Modal.Title>
                {`${modalArticle.articleNumber} - ${modalArticle.description}`}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Form>
                <Row>
                  <Col xs={12} md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Beskrivning</Form.Label>
                      <Form.Control
                        type="text"
                        defaultValue={modalArticle.description}
                        readOnly
                        disabled
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col xs={6} md={2}>
                    <Form.Group className="mb-3">
                      <Form.Label>Antal beställda</Form.Label>
                      <Form.Control
                        type="number"
                        defaultValue={modalArticle.quantity ?? ""}
                        readOnly
                        disabled
                      />
                    </Form.Group>
                  </Col>
                  <Col xs={6} md={2}>
                    <Form.Group className="mb-3">
                      <Form.Label>Antal levererade</Form.Label>
                      <Form.Control
                        type="number"
                        defaultValue={modalArticle.deliveredQuantity ?? ""}
                        readOnly
                        disabled
                      />
                    </Form.Group>
                  </Col>
                  {clientType === ClientType.Fleet && (
                    <>
                      <Col xs={6} md={2}>
                        <Form.Group className="mb-3">
                          <Form.Label>Lager från AX</Form.Label>
                          <Form.Control
                            type="text"
                            defaultValue={modalArticle.stock ?? "Ej hämtat"}
                            readOnly
                            disabled
                          />
                        </Form.Group>
                      </Col>
                      <Col xs={6} md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Meddelande från AX-export</Form.Label>
                          <Form.Control
                            type="text"
                            defaultValue={
                              modalArticle.id
                                ? (getStockStatusMessage(
                                    articleStockStatus[modalArticle.id],
                                  ) ?? "")
                                : ""
                            }
                            readOnly
                            disabled
                          />
                        </Form.Group>
                      </Col>
                    </>
                  )}
                </Row>
                <Row>
                  <Col xs={12} md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Leverantör</Form.Label>
                      <Form.Control
                        type="text"
                        value={modalArticle.supplier?.name}
                        readOnly
                        disabled
                      />
                    </Form.Group>
                  </Col>
                  <Col xs={12} md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Artikelnummer</Form.Label>
                      <Form.Control
                        type="text"
                        defaultValue={modalArticle.articleNumber}
                        readOnly
                        disabled
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col xs={12} md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Extern kommentar</Form.Label>
                      <Form.Control
                        type="text"
                        defaultValue={modalArticle.comment}
                        readOnly
                        disabled
                      />
                    </Form.Group>
                  </Col>
                  <Col xs={12} md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Intern kommentar</Form.Label>
                      <Form.Control
                        type="text"
                        defaultValue={modalArticle.internalComment}
                        readOnly
                        disabled
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col xs={12} md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Externt prefix</Form.Label>
                      <Form.Control
                        type="text"
                        defaultValue={modalArticle.externalArticleCode}
                        readOnly
                        disabled
                      />
                    </Form.Group>
                  </Col>
                  {(order.clientType === ClientType.Standard ||
                    order.clientType === ClientType.Fleet) && (
                    <Col xs={12} md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Rabatt (%)</Form.Label>
                        <Form.Control
                          type="number"
                          defaultValue={modalArticle.discount ?? ""}
                          readOnly
                          disabled
                        />
                      </Form.Group>
                    </Col>
                  )}
                </Row>
                <Row>
                  <Col xs={12} md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>IO-nummer</Form.Label>
                      <Form.Control
                        type="text"
                        defaultValue={modalArticle.purchaseOrderNumber}
                        readOnly
                        disabled
                      />
                    </Form.Group>
                  </Col>
                  <Col xs={12} md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Artikelnummer i AX</Form.Label>
                      <Form.Control
                        type="text"
                        defaultValue={modalArticle.adId}
                        readOnly
                        disabled
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col xs={12} md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Inköpspris</Form.Label>
                      <Form.Control
                        type="text"
                        defaultValue={modalArticle.purchasePrice}
                        readOnly
                        disabled
                      />
                    </Form.Group>
                  </Col>
                  <Col xs={12} md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Försäljningspris</Form.Label>
                      <Form.Control
                        type="text"
                        defaultValue={modalArticle.salesPrice}
                        readOnly
                        disabled
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col xs={12} md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>SPN</Form.Label>
                      <Form.Control
                        type="text"
                        defaultValue={modalArticle.storeSalesPrice}
                        readOnly
                        disabled
                      />
                    </Form.Group>
                  </Col>
                  <Col xs={12} md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>VSN</Form.Label>
                      <Form.Control
                        type="text"
                        defaultValue={modalArticle.workshopSalesPrice}
                        readOnly
                        disabled
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col xs={12} md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>PL URL</Form.Label>
                      <div className="mt-2">
                        {modalArticle.partUrl &&
                        modalArticle.partUrl.trim() !== "" ? (
                          <Link
                            target="_blank"
                            className="mt-1"
                            to={modalArticle.partUrl}
                          >
                            Länk
                          </Link>
                        ) : (
                          <span>-</span>
                        )}
                      </div>
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col xs={12} md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Exkluderad från order</Form.Label>
                      <Form.Check
                        type="switch"
                        checked={
                          modalArticle.excludeFromWarehousePicking || false
                        }
                        disabled
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <hr />
                <Row>
                  <Col xs={6} md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Totalt exkl. moms</Form.Label>
                      <Form.Control
                        type="text"
                        value={modalArticle.totalExVat}
                        readOnly
                        disabled
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Form>
            </Modal.Body>
            <Modal.Footer>
              <div className="float-end d-inline-flex">
                <div className="me-3">
                  <Form.Check
                    type="switch"
                    label="Granskad offert"
                    className="mt-2"
                    checked={modalArticle.reviewedQuote}
                    disabled
                    readOnly
                  />
                </div>
                <div className="me-3 mt-2">
                  {modalArticle.verified ? (
                    <Badge bg="success">Klarmarkerad</Badge>
                  ) : (
                    <Badge bg="danger">Inte klarmarkerad</Badge>
                  )}
                </div>
                <Button
                  className="mb-3"
                  variant="outline-primary"
                  onClick={closeArticleModalHandler}
                >
                  Stäng
                </Button>
              </div>
            </Modal.Footer>
          </Modal>
          <Modal show={showCustomerModal} onHide={handleCloseCustomerModal}>
            <Modal.Header closeButton>
              <Modal.Title>{order.customer?.name}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Form>
                <Row>
                  <Col xs={12} md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Kund</Form.Label>
                      <Form.Control
                        type="text"
                        value={order.customer?.name}
                        readOnly
                        disabled
                      />
                    </Form.Group>
                  </Col>
                  <Col xs={12} md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Kundnummer (AX)</Form.Label>
                      <Form.Control
                        type="text"
                        value={order.customer?.externalId ?? ""}
                        readOnly
                        disabled
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col xs={12} md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Adress</Form.Label>
                      <Form.Control
                        type="text"
                        value={order.customer?.street ?? ""}
                        readOnly
                        disabled
                      />
                    </Form.Group>
                  </Col>
                  <Col xs={12} md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>E-post</Form.Label>
                      <Form.Control
                        type="text"
                        value={order.customer?.email ?? ""}
                        readOnly
                        disabled
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col xs={12} md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Telefon</Form.Label>
                      <Form.Control
                        type="text"
                        value={order.customer?.phone}
                        readOnly
                        disabled
                      />
                    </Form.Group>
                  </Col>
                  <Col xs={12} md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Dokumentmapp</Form.Label>
                      <Form.Control
                        type="email"
                        value={order.customer?.documentFolder}
                        readOnly
                        disabled
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <Form.Group className="mb-3">
                      <Form.Label>Anteckning</Form.Label>
                      <Form.Control
                        as="textarea"
                        value={order.customer?.comment ?? ""}
                        readOnly
                        disabled
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Form>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseCustomerModal}>
                Stäng
              </Button>
            </Modal.Footer>
          </Modal>
          <Row>
            <div>
              {orderData.userId ? (
                <span className="float-end">
                  <Button
                    variant="primary"
                    href={`/orderStatus/${orderData.orderStatus?.id}/orders/edit/${orderData.id}`}
                  >
                    Lås upp <PersonFillLock />
                  </Button>
                  <span className="username-text">
                    Låst av: {orderData.userName}
                  </span>
                </span>
              ) : (
                <span className="float-end">
                  <Button
                    variant="primary"
                    className="me-2"
                    onClick={handleAssignOrder}
                  >
                    Lås order <PersonFillLock />
                  </Button>
                  <Button
                    variant="primary"
                    href={`/orderStatus/${orderData.orderStatus?.id}/orders/edit/${orderData.id}`}
                  >
                    Redigera
                  </Button>
                </span>
              )}
              {!orderData.exported && !isWaitingOrderExportResponse && (
                <>
                  {clientType === ClientType.Fleet && (
                    <>
                      <Button
                        variant="outline-primary"
                        className="float-end me-2"
                        onClick={handleCheckStock}
                        disabled={isWaitingStockResponse}
                      >
                        Hämta saldo
                      </Button>
                    </>
                  )}
                  <>
                    <Button
                      variant="primary"
                      className="float-end me-2"
                      onClick={handleShowExportModal}
                      disabled={
                        !allowedToExport || isWaitingOrderExportResponse
                      }
                    >
                      Exportera till AX
                    </Button>
                  </>
                  {!supplierExists && (
                    <Badge bg="danger float-end mt-2 me-3">
                      Leverantör krävs på antingen alla artiklar eller order för
                      export
                    </Badge>
                  )}
                  {(!allProductVerified || !allTimeVerified) && (
                    <Badge bg="danger float-end mt-2 me-3">
                      Alla artiklar måste klarmarkeras för export
                    </Badge>
                  )}
                </>
              )}
              {orderData.exported && (
                <>
                  <Button
                    variant="primary"
                    className="float-end me-2"
                    onClick={handleShowExportModal}
                    disabled={!allowedToExport || isWaitingOrderExportResponse}
                  >
                    Återexportera artiklar till AX
                  </Button>
                </>
              )}
              {orderData.exported &&
                orderData.clientType === ClientType.Standard && (
                  <>
                    <Button
                      variant="outline-primary"
                      className="float-end me-2"
                      onClick={handlePdfVerification}
                    >
                      Hämta pdf bekräftelse <FilePdf />
                    </Button>
                  </>
                )}
              <Modal show={showExportModal} onHide={handleCloseExportModal}>
                <Modal.Header closeButton>
                  <Modal.Title>Exportera order till AX</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                  Är du säker på att du vill exportera ordern till AX?
                </Modal.Body>
                <Modal.Footer>
                  <Button variant="secondary" onClick={handleCloseExportModal}>
                    Avbryt
                  </Button>
                  <Button
                    variant="primary"
                    disabled={!allowedToExport || isWaitingOrderExportResponse}
                    onClick={handleExportOrder}
                  >
                    Exportera till AX
                  </Button>
                </Modal.Footer>
              </Modal>
              <Modal
                show={showStockErrorModal}
                onHide={() => setShowStockErrorModal(false)}
              >
                <Modal.Header closeButton>
                  <Modal.Title>Exportfel</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                  Det går inte att exportera ordern eftersom någon artikel har
                  lägre saldo än antal. Kontrollera artiklarna och försök igen.
                </Modal.Body>
                <Modal.Footer>
                  <Button variant="primary" onClick={handleConfirmStockError}>
                    OK
                  </Button>
                </Modal.Footer>
              </Modal>
            </div>
            <Alert
              key="alert"
              variant="danger"
              show={displayError}
              className="mt-3"
            >
              <p>Något gick fel vid exporten till AX, vänligen försök igen.</p>
            </Alert>
          </Row>
          <hr />

          {orderStatus.orderPageType?.emailVerification && (
            <>
              {orderData.clientType === ClientType.Standard && (
                <StandardPriceProposal
                  instance={instance}
                  account={account}
                  orderId={orderId}
                  emailHistory={emailHistory}
                  isWaitingSendVerificationEmailResponse={
                    isWaitingSendOrderEmailResponse ?? false
                  }
                  isWaitingVerificationPreviewResponse={
                    isWaitingPriceProposalPreviewResponse ?? false
                  }
                  customer={orderData.customer}                  
                />
              )}
            </>
          )}
          {orderStatus.orderPageType?.emailPriceProposal && (
            <>
              {orderData.clientType === ClientType.Fleet && (
                <FleetPriceProposal
                  instance={instance}
                  account={account}
                  orderId={orderId}
                  emailHistory={emailHistory}
                  isWaitingSendOrderEmailResponse={
                    isWaitingSendOrderEmailResponse ?? false
                  }
                  isWaitingPriceProposalPreviewResponse={
                    isWaitingPriceProposalPreviewResponse ?? false
                  }
                />
              )}
            </>
          )}

          <hr />
          <div className="mt-3">
            <Accordion>
              <Accordion.Item eventKey="0">
                <Accordion.Header onClick={handleHistoryAccordion}>
                  Historik
                </Accordion.Header>
                <Accordion.Body>
                  <ListGroup as="ul">
                    {orderData?.orderHistory?.map((history) => (
                      <ListGroup.Item as="li" key={history.id}>
                        {(() => {
                          switch (history.action.toLowerCase()) {
                            case "add":
                              return <span>Skapades av </span>;
                            case "update":
                              return <span>Uppdaterades av </span>;
                            case "assign":
                              return <span>Låstes av </span>;
                            case "unassign":
                              return <span>Låstes upp av </span>;
                            case "export":
                              return <span>Exportades av </span>;
                            case "revertexport":
                              return <span>Export återställdes av </span>;
                            case "import":
                              return <span>IO/FO hämtad av </span>;
                            default:
                              return <span>-</span>;
                          }
                        })()}
                        <span>{history.userName} </span>
                        <span>
                          {" "}
                          den{" "}
                          {moment
                            .utc(history.createdAt)
                            .local()
                            .format("yyyy-MM-DD")}
                        </span>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>
          </div>
        </>
      )}
    </div>
  );
};

export default OrderView;
